/**
 * Restaurant Tables Service
 * Handles all table-related API calls
 */

import { apiService, ApiResponse } from './api.service';

/** Restaurant Table Interface */
export interface RestaurantTable {
  id: string;
  tenantId: string;
  branchId: string;
  name: string;
  capacity: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  status?: 'available' | 'occupied'; // Computed based on active orders
  activeOrdersCount?: number; // Number of active orders for this table
}

/** Create Table DTO */
export interface CreateTableDto {
  name: string;
  capacity: number;
  isActive?: boolean;
}

/** Update Table DTO */
export interface UpdateTableDto {
  name?: string;
  capacity?: number;
  isActive?: boolean;
}

class TablesService {
  /** Get all tables */
  async getTables(tenantId?: string, branchId?: string): Promise<ApiResponse<RestaurantTable[]>> {
    const params = new URLSearchParams();
    if (tenantId) params.append('tenantId', tenantId);
    if (branchId) params.append('branchId', branchId);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return apiService.get<RestaurantTable[]>(`/tables${qs}`);
  }

  /** Get single table */
  async getTableById(id: string): Promise<ApiResponse<RestaurantTable>> {
    return apiService.get<RestaurantTable>(`/tables/${id}`);
  }

  /** Create new table */
  async createTable(data: CreateTableDto): Promise<ApiResponse<RestaurantTable>> {
    return apiService.post<RestaurantTable>('/tables', data);
  }

  /** Update table */
  async updateTable(id: string, data: UpdateTableDto): Promise<ApiResponse<RestaurantTable>> {
    return apiService.patch<RestaurantTable>(`/tables/${id}`, data);
  }

  /** Delete table */
  async deleteTable(id: string): Promise<ApiResponse<void>> {
    return apiService.delete(`/tables/${id}`);
  }
}

export const tablesService = new TablesService();